import { create } from 'zustand';
import logger from '@/logger';
import {
	AgentTool,
	ToolInstructionWithTools,
	InstructionStep,
	AgentContextVariable
} from '@/pages/CustomerSupport/types';
import { useSupabaseStore } from '@/pages/CustomerSupport/supabaseStore';

interface SaveToolInstructionParams {
	id?: string;
	agentId: string;
	steps: InstructionStep[];
	enabled?: boolean;
	sortOrder?: number;
}

interface AgentToolsStore {
	tools: AgentTool[];
	instructions: ToolInstructionWithTools[];
	contextVariables: AgentContextVariable[];
	isLoading: boolean;
	error: string | null;

	loadAgentTools: () => Promise<void>;
	loadToolInstructions: (agentId: string) => Promise<void>;
	loadContextVariables: () => Promise<void>;
	saveToolInstruction: (params: SaveToolInstructionParams) => Promise<boolean>;
	deleteToolInstruction: (id: string) => Promise<boolean>;
	reorderInstructions: (agentId: string, reorderedIds: string[]) => Promise<boolean>;
	parseStepsFromInstruction: (instruction: ToolInstructionWithTools) => InstructionStep[];
	resetError: () => void;
}

function generateInstructionText(steps: InstructionStep[], availableTools: AgentTool[]): string {
	const stepTexts = steps
		.map((step) => {
			if (step.type === 'tool' && step.toolId) {
				const tool = availableTools.find((t) => t.id === step.toolId);
				return tool?.name || '';
			} else if (step.type === 'instruction' && step.text) {
				return step.text.trim();
			}
			return '';
		})
		.filter(Boolean);

	const stepsJson = JSON.stringify(steps);
	return `${stepTexts.join(' → ')}|STEPS:${stepsJson}`;
}

function extractTriggerDescription(steps: InstructionStep[]): string {
	return steps[0]?.type === 'instruction' ? steps[0].text || '' : '';
}

function parseStepsFromInstructionText(
	instructionText: string,
	availableTools: AgentTool[]
): InstructionStep[] {
	try {
		const stepsMatch = instructionText.match(/\|STEPS:(.+)$/);
		if (stepsMatch) {
			const steps = JSON.parse(stepsMatch[1]) as InstructionStep[];
			return steps.filter((s) => {
				if (s.type === 'tool') return !!s.toolId && availableTools.some((t) => t.id === s.toolId);
				return !!s.text?.trim();
			});
		}
	} catch (err) {
		logger.error('Error parsing steps from instruction text:', err);
	}

	const withoutPrefix = instructionText.replace(/^Cuando\s+[^:]+:\s*/i, '');
	const parts = withoutPrefix
		.split('→')
		.map((p) => p.trim())
		.filter(Boolean);

	const steps: InstructionStep[] = [];
	for (const part of parts) {
		const tool = availableTools.find((t) => part.includes(t.name));
		if (tool) {
			steps.push({ type: 'tool', toolId: tool.id });
		} else if (part.trim()) {
			steps.push({ type: 'instruction', text: part.trim() });
		}
	}

	return steps;
}

export const useAgentToolsStore = create<AgentToolsStore>((set, get) => ({
	tools: [],
	instructions: [],
	contextVariables: [],
	isLoading: false,
	error: null,

	loadAgentTools: async () => {
		try {
			set({ isLoading: true, error: null });
			const supabase = useSupabaseStore.getState().supabase;

			const { data, error: fetchError } = await supabase
				.from('agent_tools')
				.select('*')
				.or('is_active.is.null,is_active.eq.true')
				.order('category', { ascending: true })
				.order('name', { ascending: true });

			if (fetchError) {
				logger.error('Error loading agent tools:', fetchError);
				set({ error: fetchError.message, isLoading: false });
				return;
			}

			set({
				tools: data || [],
				isLoading: false
			});
		} catch (err) {
			logger.error('Error loading agent tools:', err);
			set({
				error: 'Failed to load agent tools',
				isLoading: false
			});
		}
	},

	loadToolInstructions: async (agentId: string) => {
		try {
			set({ isLoading: true, error: null });
			const supabase = useSupabaseStore.getState().supabase;

			const { data: instructions, error: fetchError } = await supabase
				.from('organization_agent_tool_instructions')
				.select('*')
				.eq('agent_id', agentId)
				.eq('enabled', true)
				.order('sort_order', { ascending: true });

			if (fetchError) {
				logger.error('Error loading tool instructions:', fetchError);
				set({ error: fetchError.message, isLoading: false });
				return;
			}

			if (!instructions || instructions.length === 0) {
				set({
					instructions: [],
					isLoading: false
				});
				return;
			}

			const allToolIds = [...new Set(instructions.flatMap((i) => i.tool_ids))];
			const { data: tools } = await supabase.from('agent_tools').select('*').in('id', allToolIds);

			const instructionsWithTools: ToolInstructionWithTools[] = instructions.map((instruction) => ({
				...instruction,
				tools: (tools || []).filter((t) => instruction.tool_ids.includes(t.id))
			}));

			set({
				instructions: instructionsWithTools,
				isLoading: false
			});
		} catch (err) {
			logger.error('Error loading tool instructions:', err);
			set({
				error: 'Failed to load tool instructions',
				isLoading: false
			});
		}
	},

	loadContextVariables: async () => {
		try {
			const supabase = useSupabaseStore.getState().supabase;

			const { data, error: fetchError } = await supabase
				.from('agent_context_variables')
				.select('*')
				.eq('is_active', true)
				.order('name', { ascending: true });

			if (fetchError) {
				logger.error('Error loading context variables:', fetchError);
				set({ error: fetchError.message });
				return;
			}

			set({
				contextVariables: data || []
			});
		} catch (err) {
			logger.error('Error loading context variables:', err);
			set({
				error: 'Failed to load context variables'
			});
		}
	},

	saveToolInstruction: async (params: SaveToolInstructionParams) => {
		try {
			set({ error: null });
			const supabase = useSupabaseStore.getState().supabase;

			const { tools } = get();
			const toolIds = params.steps
				.filter((s) => s.type === 'tool' && s.toolId)
				.map((s) => s.toolId!);
			const instructionText = generateInstructionText(params.steps, tools);
			const triggerDescription = extractTriggerDescription(params.steps);

			const body = {
				id: params.id,
				agent_id: params.agentId,
				tool_ids: toolIds,
				trigger_description: triggerDescription,
				instruction_text: instructionText,
				enabled: params.enabled ?? true,
				sort_order: params.sortOrder ?? 0
			};

			const { data, error } = await supabase.functions.invoke('upsert-tool-instruction', {
				body
			});

			if (error) {
				logger.error('Error saving tool instruction:', error);
				set({ error: error.message });
				return false;
			}

			if (data?.success === false) {
				logger.error('Error saving tool instruction:', data.error);
				set({ error: data.error });
				return false;
			}

			await get().loadToolInstructions(params.agentId);
			return true;
		} catch (err) {
			logger.error('Error saving tool instruction:', err);
			set({ error: 'Failed to save tool instruction' });
			return false;
		}
	},

	deleteToolInstruction: async (id: string) => {
		try {
			set({ error: null });
			const supabase = useSupabaseStore.getState().supabase;

			const { error: deleteError } = await supabase
				.from('organization_agent_tool_instructions')
				.delete()
				.eq('id', id);

			if (deleteError) {
				logger.error('Error deleting tool instruction:', deleteError);
				set({ error: deleteError.message });
				return false;
			}

			const currentInstructions = get().instructions;
			const deletedInstruction = currentInstructions.find((i) => i.id === id);
			if (deletedInstruction) {
				await get().loadToolInstructions(deletedInstruction.agent_id);
			}

			return true;
		} catch (err) {
			logger.error('Error deleting tool instruction:', err);
			set({ error: 'Failed to delete tool instruction' });
			return false;
		}
	},

	reorderInstructions: async (organizationId: string, reorderedIds: string[]) => {
		try {
			set({ error: null });
			const supabase = useSupabaseStore.getState().supabase;

			const updates = reorderedIds.map((id, index) => ({
				id,
				sort_order: index
			}));

			for (const update of updates) {
				const { error: updateError } = await supabase
					.from('organization_agent_tool_instructions')
					.update({ sort_order: update.sort_order, updated_at: new Date().toISOString() })
					.eq('id', update.id);

				if (updateError) {
					logger.error('Error reordering instructions:', updateError);
					set({ error: updateError.message });
					return false;
				}
			}

			await get().loadToolInstructions(organizationId);
			return true;
		} catch (err) {
			logger.error('Error reordering instructions:', err);
			set({ error: 'Failed to reorder instructions' });
			return false;
		}
	},

	parseStepsFromInstruction: (instruction: ToolInstructionWithTools) => {
		const { tools } = get();
		return parseStepsFromInstructionText(instruction.instruction_text, tools);
	},

	resetError: () => {
		set({ error: null });
	}
}));
